import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconArrowLeft,
  IconCalendarEvent,
  IconCategory,
  IconCircleDot,
  IconClock,
  IconCreditCard,
  IconFileSearch,
  IconReportAnalytics,
} from '@tabler/icons-react';
import {
  DETAIL_TABS,
  formatExpenseDateCompact,
  getStatusMeta,
  STATUS_META,
} from '../utils/reembolsosStatus';
import {
  expenseMatchesPeriodKey,
  expenseMatchesSliceFilters,
  formatPaymentType,
  listTechSelectablePeriods,
  periodKeyOf,
} from '../utils/reembolsosFormat';
import { BoardClickUpFilterChipDropdown } from '../../board/toolbar/BoardClickUpFilterChipDropdown';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { BoardToolbarRefreshClickUpButton } from '../../board/toolbar/BoardToolbarRefreshClickUpButton';
import GestaoReembolsosDetailReportModal from './GestaoReembolsosDetailReportModal';
import GestaoReembolsosTimelineModal from './GestaoReembolsosTimelineModal';
import {
  canApprove,
  canPay,
  canReject,
  ExpenseStatusActions,
  GestaoReembolsosReceiptModal,
  GestaoReembolsosReviewNoteModal,
  GrExpenseCheckbox,
  ObsCell,
} from './GestaoReembolsosDetailActions';
import { GrHoverTooltip } from './GestaoReembolsosTooltip';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';

function Avatar({ initials, color, photoUrl, size = 26 }) {
  const [imgErr, setImgErr] = useState(false);
  const showImg = Boolean(photoUrl) && !imgErr;
  return (
    <span
      className="gr-avatar"
      style={{
        width: size,
        height: size,
        background: showImg ? 'transparent' : color || '#7c4dff',
      }}
      aria-hidden
    >
      {showImg ? (
        <img src={photoUrl} alt="" className="gr-avatar-img" onError={() => setImgErr(true)} />
      ) : (
        <span className="gr-avatar-initials" style={{ fontSize: size > 28 ? 12 : 10.5 }}>
          {initials || '?'}
        </span>
      )}
    </span>
  );
}

function toggleSelection(list, value) {
  if (list.includes(value)) return list.filter((x) => x !== value);
  return [...list, value];
}

const TABLE_COLUMNS = [
  { key: 'date', label: 'Data' },
  { key: 'code', label: 'Código' },
  { key: 'service', label: 'Serviço' },
  { key: 'description', label: 'Observação' },
  { key: 'category', label: 'Categoria' },
  { key: 'amount', label: 'Valor' },
  { key: 'payment', label: 'Pagamento' },
  { key: 'receipt', label: 'Anexo' },
  { key: 'status', label: 'Status' },
];

function rowId(row, idx) {
  if (row?.id != null && String(row.id) !== '') return String(row.id);
  return `${row?.code || 'x'}-${row?.expenseDate || ''}-${idx}`;
}

function sortValue(row, key) {
  switch (key) {
    case 'date':
      return String(row.expenseDate || '');
    case 'code':
      return String(row.code || '').toLowerCase();
    case 'service':
      return String(row.operationCode || '').toLowerCase();
    case 'description':
      return String(row.description || '').toLowerCase();
    case 'category':
      return String(row.expenseTypeName || '').toLowerCase();
    case 'amount':
      return Number(row.amount) || 0;
    case 'payment':
      return formatPaymentType(row.paymentType).toLowerCase();
    case 'receipt':
      return row.hasReceipt && row.receiptUrl ? 1 : 0;
    case 'status':
      return String(getStatusMeta(row.statusKey).label || row.statusKey || '').toLowerCase();
    default:
      return '';
  }
}

/**
 * Detalhe do técnico: tabela de lançamentos (LCMY_GO_EXPENSES).
 */
export default function GestaoReembolsosDetail({
  tech,
  expenses = [],
  onBack,
  onRefresh,
  refreshing = false,
  onStatusChange,
  flashIds,
  actionBusy = false,
  processingIds,
  canApproveReject = false,
  canPay: canPayPerm = false,
}) {
  const [tab, setTab] = useState('todos');
  const [query, setQuery] = useState('');
  const [openFilter, setOpenFilter] = useState(null);
  const [periodKey, setPeriodKey] = useState('');
  const [catsSel, setCatsSel] = useState([]);
  const [statusSel, setStatusSel] = useState([]);
  const [paySel, setPaySel] = useState([]);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [reportOpen, setReportOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTargets, setReviewTargets] = useState([]);
  const [rejectMode, setRejectMode] = useState('review'); /* review | definitive */
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [timelinePreview, setTimelinePreview] = useState(null);
  const [localBusy, setLocalBusy] = useState(false);

  const periods = useMemo(() => listTechSelectablePeriods(expenses), [expenses]);
  const periodKeysSig = useMemo(() => periods.map((p) => p.key).join('\n'), [periods]);

  const periodByLabel = useMemo(() => {
    const map = {};
    for (const p of periods) {
      map[p.label] = p;
    }
    return map;
  }, [periods]);

  const periodLabelOptions = useMemo(() => periods.map((p) => p.label), [periods]);

  useEffect(() => {
    setTab('todos');
    setQuery('');
    setCatsSel([]);
    setStatusSel([]);
    setPaySel([]);
    setOpenFilter(null);
    setSortKey('date');
    setSortDir('desc');
    setReportOpen(false);
    setSelected(new Set());
    setReviewOpen(false);
    setReviewTargets([]);
    setRejectMode('review');
    setReceiptPreview(null);
    setTimelinePreview(null);
  }, [tech?.codtech]);

  useEffect(() => {
    setPeriodKey((prev) => {
      if (prev && periods.some((p) => p.key === prev)) return prev;
      const preferred = periodKeyOf(tech?.periodStart, tech?.periodEnd);
      if (preferred && periods.some((p) => p.key === preferred)) return preferred;
      return periods[0]?.key || '';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- periods via periodKeysSig
  }, [tech?.codtech, tech?.periodStart, tech?.periodEnd, periodKeysSig]);

  const selectedPeriodLabel = useMemo(() => {
    const p = periods.find((x) => x.key === periodKey);
    return p?.label || '';
  }, [periods, periodKey]);

  const selectedPeriodIsActive = useMemo(() => {
    const p = periods.find((x) => x.key === periodKey);
    return Boolean(p?.isActive);
  }, [periods, periodKey]);

  const periodExpenses = useMemo(() => {
    if (!periodKey) return expenses;
    return expenses.filter((e) => expenseMatchesPeriodKey(e, periodKey));
  }, [expenses, periodKey]);

  const categoryOptions = useMemo(() => {
    const names = new Set();
    for (const e of periodExpenses) {
      const n = String(e.expenseTypeName || '').trim();
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [periodExpenses]);

  const statusOptions = useMemo(
    () => Object.keys(STATUS_META).map((key) => STATUS_META[key].label),
    []
  );

  const paymentOptions = useMemo(() => {
    const names = new Set();
    for (const e of periodExpenses) {
      const n = formatPaymentType(e.paymentType);
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [periodExpenses]);

  const statusLabelToKey = useMemo(() => {
    const map = {};
    for (const [key, meta] of Object.entries(STATUS_META)) {
      map[meta.label] = key;
    }
    return map;
  }, []);

  const sliceFilter = useMemo(() => {
    const statusKeys = statusSel
      .map((label) => statusLabelToKey[label])
      .filter(Boolean);
    return {
      categories: catsSel,
      statusKeys,
      paymentLabels: paySel,
      active: Boolean(catsSel.length || statusKeys.length || paySel.length),
    };
  }, [catsSel, statusSel, paySel, statusLabelToKey]);

  const filteredBase = useMemo(() => {
    if (!sliceFilter.active) return periodExpenses;
    return periodExpenses.filter((e) => expenseMatchesSliceFilters(e, sliceFilter));
  }, [periodExpenses, sliceFilter]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = filteredBase.filter((e) => {
      if (tab !== 'todos' && e.statusKey !== tab) return false;
      if (!q) return true;
      const hay = `${e.description || ''} ${e.code || ''} ${e.operationCode || ''} ${e.expenseTypeName || ''} ${e.paymentType || ''}`.toLowerCase();
      return hay.includes(q);
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      if (typeof va === 'number' && typeof vb === 'number') {
        if (va === vb) return 0;
        return va < vb ? -dir : dir;
      }
      const cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' });
      return cmp * dir;
    });
  }, [filteredBase, tab, query, sortKey, sortDir]);

  const listIds = useMemo(() => list.map((row, idx) => rowId(row, idx)), [list]);

  const selectedRows = useMemo(
    () => list.filter((row, idx) => selected.has(rowId(row, idx))),
    [list, selected]
  );

  const bulkCanApprove = canApproveReject && selectedRows.some((r) => canApprove(r.statusKey));
  const bulkCanReject = canApproveReject && selectedRows.some((r) => canReject(r.statusKey));
  const bulkCanPay = canPayPerm && selectedRows.some((r) => canPay(r.statusKey));
  const hasStatusActions = canApproveReject || canPayPerm;
  const hasSelection = selected.size > 0;
  const allVisibleSelected =
    listIds.length > 0 && listIds.every((id) => selected.has(id));
  const someVisibleSelected =
    listIds.some((id) => selected.has(id)) && !allVisibleSelected;

  const tabCounts = useMemo(() => {
    const counts = { todos: filteredBase.length };
    for (const e of filteredBase) {
      counts[e.statusKey] = (counts[e.statusKey] || 0) + 1;
    }
    return counts;
  }, [filteredBase]);

  const totalLabel = useMemo(() => {
    const amt = list.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return amt.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [list]);

  const openChange = useCallback((key) => (open) => {
    setOpenFilter(open ? key : null);
  }, []);

  const toggleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'date' || key === 'amount' ? 'desc' : 'asc');
  }, [sortKey]);

  const openReceipt = (row) => {
    if (!row?.receiptUrl) return;
    setReceiptPreview({
      url: row.receiptUrl,
      code: row.code,
      expenseTypeName: row.expenseTypeName,
      amountLabel: row.amountLabel,
    });
  };

  const busy = Boolean(actionBusy || localBusy || refreshing);

  const applyAction = useCallback(
    async (ids, action, reason) => {
      if (!ids?.length || busy) return { ok: false };
      setLocalBusy(true);
      try {
        const res = await onStatusChange?.(ids.map(String), { action, reason });
        if (res?.ok !== false) {
          setSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(String(id)));
            return next;
          });
          return { ok: true, ...res };
        }
        return { ok: false, ...res };
      } finally {
        setLocalBusy(false);
      }
    },
    [onStatusChange, busy]
  );

  const openRejectModal = (ids, mode) => {
    setRejectMode(mode);
    setReviewTargets(ids.map(String));
    setReviewOpen(true);
  };

  const bulkIdsWhere = (pred) =>
    list
      .map((row, idx) => ({ row, id: listIds[idx] }))
      .filter(({ row }) => pred(row))
      .filter(({ id }) => selected.has(id))
      .map(({ id }) => id);

  const toggleRow = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllVisible = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) listIds.forEach((id) => next.add(id));
      else listIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  return (
    <div className="gr-detail">
      <div className="filters-bar gr-filters-bar">
        <div className="filter-buttons">
          <div className="filter-group board-toolbar-filter-group">
            <BoardClickUpFilterChipDropdown
              label="Período"
              icon={IconCalendarEvent}
              options={periodLabelOptions}
              selected={selectedPeriodLabel ? [selectedPeriodLabel] : []}
              selectionDisplay={selectedPeriodLabel}
              isOpen={openFilter === 'periodo'}
              onOpenChange={openChange('periodo')}
              onToggleItem={(label) => {
                const p = periodByLabel[label];
                if (p) setPeriodKey(p.key);
                setOpenFilter(null);
              }}
              onToggleAll={() => {}}
              onClear={() => {
                if (periods[0]) setPeriodKey(periods[0].key);
              }}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={220}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="períodos"
              listAriaLabel="Períodos do técnico"
              popoverClassName="board-search-filters-popover--periodo gr-module-popover"
              emptyMessage="Nenhum período encontrado"
              showSearch={periodLabelOptions.length > 8}
              showSimpleBulkMenu={false}
            />
            <span className="board-toolbar-separator" aria-hidden="true">
              |
            </span>
            <BoardClickUpFilterChipDropdown
              label="Categoria"
              icon={IconCategory}
              options={categoryOptions}
              selected={catsSel}
              isOpen={openFilter === 'categoria'}
              onOpenChange={openChange('categoria')}
              onToggleItem={(v) => setCatsSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setCatsSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setCatsSel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={238}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="categorias"
              listAriaLabel="Categorias de despesa"
              popoverClassName="board-search-filters-popover--categoria gr-module-popover"
              emptyMessage="Nenhuma categoria encontrada"
            />
            <BoardClickUpFilterChipDropdown
              label="Status"
              icon={IconCircleDot}
              options={statusOptions}
              selected={statusSel}
              isOpen={openFilter === 'status'}
              onOpenChange={openChange('status')}
              onToggleItem={(v) => setStatusSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setStatusSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setStatusSel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={238}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="status"
              listAriaLabel="Status da despesa"
              popoverClassName="board-search-filters-popover--status gr-module-popover"
              emptyMessage="Nenhum status encontrado"
              showSearch={false}
            />
            <BoardClickUpFilterChipDropdown
              label="Forma de pagamento"
              icon={IconCreditCard}
              options={paymentOptions}
              selected={paySel}
              isOpen={openFilter === 'pagamento'}
              onOpenChange={openChange('pagamento')}
              onToggleItem={(v) => setPaySel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setPaySel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setPaySel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={260}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="formas"
              listAriaLabel="Formas de pagamento"
              popoverClassName="board-search-filters-popover--pagamento gr-module-popover"
              emptyMessage="Nenhuma forma encontrada"
            />
          </div>

          <div className="search-container board-toolbar-right">
            <BoardToolbarExpandSearch
              value={query}
              onChange={setQuery}
              placeholder="Código, descrição, categoria…"
              ariaLabel="Buscar lançamentos"
              forceExpanded
            />
            <BoardToolbarRefreshClickUpButton
              onClick={() => onRefresh?.()}
              disabled={Boolean(refreshing) || !onRefresh}
              isRefreshing={Boolean(refreshing)}
            />
            <div className="board-clickup-filter-chip-anchor">
              <button
                type="button"
                className="board-clickup-filter-chip"
                aria-label="Gerar relatório da tabela"
                onClick={() => setReportOpen(true)}
              >
                <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                  <IconReportAnalytics size={13} stroke={1.5} />
                </span>
                <span className="board-clickup-filter-chip-text">
                  <span className="board-clickup-filter-chip-label">Relatório</span>
                </span>
              </button>
            </div>
            <span className="board-toolbar-separator" aria-hidden="true">
              |
            </span>
            <div className="board-clickup-filter-chip-anchor gr-detail-back-chip">
              <button
                type="button"
                className="board-clickup-filter-chip"
                aria-label="Voltar para equipes"
                onClick={onBack}
              >
                <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                  <IconArrowLeft size={13} stroke={1.5} />
                </span>
                <span className="board-clickup-filter-chip-text">
                  <span className="board-clickup-filter-chip-label gr-detail-back-label">
                    Voltar
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="gr-detail-top">
        <div className="gr-detail-identity">
          <Avatar
            initials={tech?.initials}
            color={tech?.avatarColor}
            photoUrl={tech?.profilePhotoUrl}
            size={30}
          />
          <div className="gr-detail-id-text">
            <div className="gr-detail-name-row">
              <div className="gr-detail-name">{tech?.name || 'Técnico'}</div>
              {selectedPeriodIsActive ? (
                <GrHoverTooltip content="O período atual ainda está em andamento" delayMs={180}>
                  <span className="gr-detail-period-hint is-active">
                    <span className="gr-detail-period-hint-text">Período vigente</span>
                  </span>
                </GrHoverTooltip>
              ) : null}
            </div>
            <div className="gr-detail-path">
              {[tech?.coordinatorName, tech?.areaName].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="gr-detail-tabs-row">
        <div className="gr-detail-tabs-line">
          <div className="gr-detail-tabs" role="tablist">
            {DETAIL_TABS.map((t) => {
              const on = tab === t.key;
              const n = tabCounts[t.key] || 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={`gr-detail-tab${on ? ' is-active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                  <span className="gr-detail-tab-badge">{n}</span>
                </button>
              );
            })}
          </div>

          <div
            className={`gr-detail-bulk${hasSelection ? ' is-visible' : ''}`}
            aria-hidden={!hasSelection}
          >
            <span className="gr-detail-bulk-count">
              {selected.size} {selected.size === 1 ? 'selecionado' : 'selecionados'}
            </span>
            <button
              type="button"
              className="gr-action-btn"
              onClick={() => setSelected(new Set())}
              tabIndex={hasSelection ? 0 : -1}
            >
              Limpar
            </button>
            <ExpenseStatusActions
              showApprove={bulkCanApprove}
              showReject={bulkCanReject}
              showPay={bulkCanPay}
              disabled={busy}
              busy={actionBusy}
              onApprove={() => {
                const ids = bulkIdsWhere((r) => canApprove(r.statusKey));
                if (ids.length) applyAction(ids, 'approve');
              }}
              onPay={() => {
                const ids = bulkIdsWhere((r) => canPay(r.statusKey));
                if (ids.length) applyAction(ids, 'pay');
              }}
              onRejectDefinitive={() => {
                const ids = bulkIdsWhere((r) => canReject(r.statusKey));
                if (ids.length) openRejectModal(ids, 'definitive');
              }}
              onRejectReview={() => {
                const ids = bulkIdsWhere((r) => canReject(r.statusKey));
                if (ids.length) openRejectModal(ids, 'review');
              }}
            />
          </div>
        </div>
        <div className="gr-detail-total">
          <span className="gr-detail-total-label">Selecionado</span>
          <span className="gr-detail-total-value">R$ {totalLabel}</span>
        </div>
      </div>

      <div className="gr-detail-table-wrap">
        {!list.length ? (
          <div className="gr-empty">Nenhum lançamento para este filtro.</div>
        ) : (
          <div
            className={`gr-detail-table${hasSelection ? ' is-bulk-selection' : ''}`}
            role="table"
          >
            <div className="gr-detail-thead" role="row">
              <span className="gr-detail-th-check">
                <GrHoverTooltip content="Selecionar todos visíveis" delayMs={200}>
                  <span className="gr-tip-inline">
                    <GrExpenseCheckbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      title="Selecionar todos visíveis"
                      onChange={toggleAllVisible}
                    />
                  </span>
                </GrHoverTooltip>
              </span>
              {TABLE_COLUMNS.map((col) => {
                const active = sortKey === col.key;
                const ariaSort = active
                  ? sortDir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
                return (
                  <button
                    key={col.key}
                    type="button"
                    className={`gr-detail-th${active ? ' is-sorted' : ''}`}
                    onClick={() => toggleSort(col.key)}
                    aria-sort={ariaSort}
                  >
                    <span>{col.label}</span>
                    {active ? (
                      <span className="gr-detail-th-dir" aria-hidden>
                        {sortDir === 'asc' ? '↑' : '↓'}
                      </span>
                    ) : null}
                  </button>
                );
              })}
              <span className="gr-detail-th gr-detail-th-static gr-detail-th-history" aria-hidden />
              <span className="gr-detail-th gr-detail-th-static gr-detail-th-actions">
                Ações
              </span>
            </div>
            {list.map((row, idx) => {
              const id = rowId(row, idx);
              const st = getStatusMeta(row.statusKey);
              const isSel = selected.has(id);
              const isFlash = flashIds?.has?.(String(id)) || flashIds?.has?.(String(row.id));
              const isProcessing =
                processingIds?.has?.(String(id)) || processingIds?.has?.(String(row.id));
              return (
                <div
                  key={id}
                  className={`gr-detail-row${isSel ? ' is-selected' : ''}${isFlash ? ' is-status-flash' : ''}${isProcessing ? ' is-processing' : ''}`}
                  role="row"
                  aria-busy={isProcessing || undefined}
                >
                  <span className="gr-detail-check-cell">
                    <GrHoverTooltip content="Selecionar lançamento" delayMs={200}>
                      <span className="gr-tip-inline">
                        <GrExpenseCheckbox
                          checked={isSel}
                          title="Selecionar lançamento"
                          onChange={(c) => toggleRow(id, c)}
                        />
                      </span>
                    </GrHoverTooltip>
                  </span>
                  <span className="gr-detail-date">{formatExpenseDateCompact(row.expenseDate)}</span>
                  <span className="gr-detail-code">{row.code || '—'}</span>
                  <span className="gr-detail-service">{row.operationCode || '—'}</span>
                  <ObsCell text={row.description || '—'} />
                  <span className="gr-detail-cat">{row.expenseTypeName || '—'}</span>
                  <span className="gr-detail-amount">{row.amountLabel}</span>
                  <span className="gr-detail-pay">{formatPaymentType(row.paymentType) || '—'}</span>
                  <span className="gr-detail-files">
                    {row.hasReceipt && row.receiptUrl ? (
                      <GrHoverTooltip content="Ver comprovante" delayMs={180}>
                        <span className="gr-tip-inline">
                          <button
                            type="button"
                            className="gr-receipt-btn"
                            onClick={() => openReceipt(row)}
                            disabled={busy}
                          >
                            <IconFileSearch size={16} stroke={1.75} />
                          </button>
                        </span>
                      </GrHoverTooltip>
                    ) : (
                      <span className="gr-receipt-empty">—</span>
                    )}
                  </span>
                  <span className="gr-detail-status-cell">
                    <span className="gr-status-pill is-plain" style={{ color: st.fg }}>
                      <span className="gr-status-dot" style={{ background: st.dot }} />
                      {st.label}
                    </span>
                  </span>
                  <span className="gr-detail-history-cell">
                    {row.hasTimeline ? (
                      <GrHoverTooltip content="Ver ocorrências" delayMs={180}>
                        <span className="gr-tip-inline">
                          <button
                            type="button"
                            className="gr-history-btn"
                            onClick={() =>
                              setTimelinePreview({
                                id: row.id,
                                code: row.code,
                                amountLabel: row.amountLabel,
                              })
                            }
                            disabled={busy}
                            aria-label="Ver ocorrências"
                          >
                            <IconClock size={16} stroke={1.75} />
                          </button>
                        </span>
                      </GrHoverTooltip>
                    ) : (
                      <span className="gr-history-empty" aria-hidden>
                        —
                      </span>
                    )}
                  </span>
                  <span className="gr-detail-actions-cell">
                    {!hasSelection && hasStatusActions ? (
                      <ExpenseStatusActions
                        statusKey={row.statusKey}
                        showApprove={canApproveReject && canApprove(row.statusKey)}
                        showReject={canApproveReject && canReject(row.statusKey)}
                        showPay={canPayPerm && canPay(row.statusKey)}
                        disabled={busy}
                        busy={isProcessing}
                        onApprove={() => applyAction([id], 'approve')}
                        onPay={() => applyAction([id], 'pay')}
                        onRejectDefinitive={() => openRejectModal([id], 'definitive')}
                        onRejectReview={() => openRejectModal([id], 'review')}
                      />
                    ) : !hasSelection ? (
                      <span className="gr-actions-empty">—</span>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="gr-detail-footer">
        <span>
          {list.length} de {periodExpenses.length} lançamentos
        </span>
        <span>Exibindo R$ {totalLabel}</span>
      </div>

      <GestaoReembolsosDetailReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        tech={tech}
        rows={list}
        totalLabel={totalLabel}
        periodLabel={selectedPeriodLabel}
      />

      <GestaoReembolsosReviewNoteModal
        open={reviewOpen}
        mode={rejectMode === 'definitive' ? 'definitive' : 'review'}
        loading={busy}
        onClose={() => {
          if (busy) return;
          setReviewOpen(false);
          setReviewTargets([]);
        }}
        onConfirm={async (note) => {
          const action =
            rejectMode === 'definitive' ? 'reject_definitive' : 'reject_review';
          const res = await applyAction(reviewTargets, action, note);
          if (res?.ok !== false) {
            setReviewOpen(false);
            setReviewTargets([]);
          }
        }}
      />

      <GestaoReembolsosReceiptModal
        open={Boolean(receiptPreview?.url)}
        onClose={() => setReceiptPreview(null)}
        url={receiptPreview?.url}
        meta={receiptPreview}
      />

      <GestaoReembolsosTimelineModal
        open={timelinePreview != null}
        expenseId={timelinePreview?.id}
        expenseMeta={timelinePreview}
        onClose={() => setTimelinePreview(null)}
      />
    </div>
  );
}
